import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sprout, ArrowRight, AlertTriangle } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const forcedReason = params.get("forced") ? params.get("reason") : null;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
      nav("/dashboard");
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      {/* Left panel */}
      <div
        className="hidden md:flex hero-grain relative items-end p-12"
        style={{
          backgroundImage:
            "radial-gradient(ellipse at top, rgba(15,29,20,0.55), transparent 60%), radial-gradient(ellipse at bottom right, rgba(15,29,20,0.5), transparent 60%), url('/images/aerial_render.jpg')",
        }}
      >
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 text-white max-w-md">
          <div className="text-[11px] tracking-[0.3em] uppercase text-white/70 mb-4">Sales Portal</div>
          <h2 className="font-heading text-5xl leading-none">Anuttara Township<br />Phase 2</h2>
          <p className="mt-6 text-white/80 text-sm">Verified plot inventory, interactive layouts, and end-to-end booking control.</p>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center px-6 py-16 bg-white">
        <div className="w-full max-w-sm">
          <Link to="/" className="flex items-center gap-3 mb-10">
            <img src="/images/anuttara_logo.jpg" alt="Anuttara" className="h-11 w-11 object-cover rounded-sm" />
            <div className="font-heading font-semibold">Anuttara</div>
          </Link>
          <h1 className="font-heading text-3xl">Sign in</h1>
          <p className="text-sm text-[#5C6653] mt-2">Access the plot inventory & booking portal.</p>

          {forcedReason && (
            <div data-testid="forced-logout-banner" className="mt-6 flex items-start gap-2 text-sm text-red-800 bg-red-50 border border-red-200 px-3 py-2 rounded-sm">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <div>You were signed out because your {forcedReason}.</div>
            </div>
          )}

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-sm font-medium">Email</Label>
              <Input
                id="email"
                type="email"
                required
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 rounded-sm"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-sm font-medium">Password</Label>
              <Input
                id="password"
                type="password"
                required
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 rounded-sm"
                placeholder="••••••••"
              />
            </div>
            {err && (
              <div data-testid="login-error" className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2">
                {err}
              </div>
            )}
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full rounded-sm bg-[#234B36] hover:bg-[#1A3A29] text-white h-11"
            >
              {loading ? "Signing in…" : (<>Sign in <ArrowRight className="h-4 w-4 ml-2" /></>)}
            </Button>
          </form>

          <div className="mt-6 text-sm text-[#5C6653]">
            New Channel Partner?{" "}
            <Link to="/register" data-testid="login-goto-register" className="text-[#234B36] underline underline-offset-4">Create an account</Link>
          </div>

        </div>
      </div>
    </div>
  );
}
